import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Routes, Route } from 'react-router'
import {
  useAuth,
  useProfileSync,
  RequireRole,
  RequirePermission,
  UnauthorizedPage,
} from '@structural-climbing/auth'
import { FullPageSpinner } from '@structural-climbing/ui'
import { RootLayout } from './layouts/RootLayout'
import { GymLayout } from './layouts/GymLayout'
import { DashboardPage } from './pages/DashboardPage'
import { MembersPage } from './pages/MembersPage'
import { MyMembersPage } from './pages/MyMembersPage'
import { RequestsPage } from './pages/RequestsPage'
import { PlansPage } from './pages/PlansPage'
import { ScanPage } from './pages/ScanPage'
import { SettingsPage } from './pages/SettingsPage'
import { ActivityPage } from './pages/ActivityPage'
import { TeamPage } from './pages/TeamPage'
import { RoutesPage } from './pages/RoutesPage'
import { CompetitionsPage } from './pages/CompetitionsPage'
import { CompetitionDetailPage } from './pages/CompetitionDetailPage'
import { NewCompetitionPage } from './pages/NewCompetitionPage'
import { CalendarPage } from './pages/CalendarPage'
import { NewsPage } from './pages/NewsPage'
import { ProductsPage } from './pages/ProductsPage'
import { StatisticsPage } from './pages/StatisticsPage'
import { InvoicesPage } from './pages/InvoicesPage'
import { SubscriptionsPage } from './pages/SubscriptionsPage'
import { ExportsPage } from './pages/ExportsPage'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { LogoutPage } from './pages/LogoutPage'
import { GymLoginPage } from './pages/GymLoginPage'
import { DefaultRedirectPage } from './pages/DefaultRedirectPage'

export function App() {
  const { isLoading, user } = useAuth()
  useProfileSync()

  useEffect(() => {
    if (user) Sentry.setUser({ id: user.sub, email: user.email })
    else Sentry.setUser(null)
  }, [user])

  if (isLoading) return <FullPageSpinner />

  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please reload the page.</p>}>
      <Routes>
        {/* Public / auth pages — no slug */}
        <Route element={<RootLayout />}>
          <Route index element={<DefaultRedirectPage />} />
          <Route path="login" element={<GymLoginPage />} />
          <Route path="unauthorized" element={<UnauthorizedPage portalName="Gym Portal" />} />
          <Route path="accept-invite" element={<AcceptInvitePage />} />
          <Route path="logout" element={<LogoutPage />} />
        </Route>

        {/* Slug-scoped gym pages */}
        <Route path=":slug" element={<GymLayout />}>
          <Route
            path="dashboard"
            element={
              <RequirePermission staffKey="dashboard">
                <DashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="check-in"
            element={
              <RequirePermission staffKey="check_in">
                <ScanPage />
              </RequirePermission>
            }
          />
          <Route
            path="members"
            element={
              <RequirePermission staffKey="members">
                <MembersPage />
              </RequirePermission>
            }
          />
          <Route
            path="my-members"
            element={
              <RequirePermission staffKey="my_members">
                <MyMembersPage />
              </RequirePermission>
            }
          />
          <Route
            path="requests"
            element={
              <RequirePermission staffKey="requests">
                <RequestsPage />
              </RequirePermission>
            }
          />
          <Route
            path="subscriptions"
            element={
              <RequirePermission staffKey="subscriptions">
                <SubscriptionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="plans"
            element={
              <RequirePermission staffKey="plans">
                <PlansPage />
              </RequirePermission>
            }
          />
          <Route
            path="settings"
            element={
              <RequireRole role={['gym_owner']}>
                <SettingsPage />
              </RequireRole>
            }
          />
          <Route
            path="activity"
            element={
              <RequireRole role={['gym_owner']}>
                <ActivityPage />
              </RequireRole>
            }
          />
          <Route
            path="team"
            element={
              <RequireRole role={['gym_owner']}>
                <TeamPage />
              </RequireRole>
            }
          />
          <Route
            path="routes"
            element={
              <RequirePermission staffKey="routes">
                <RoutesPage />
              </RequirePermission>
            }
          />
          <Route
            path="competitions"
            element={
              <RequirePermission staffKey="competitions">
                <CompetitionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="competitions/new"
            element={
              <RequirePermission staffKey="competitions">
                <NewCompetitionPage />
              </RequirePermission>
            }
          />
          <Route
            path="competitions/:competitionId"
            element={
              <RequirePermission staffKey="competitions">
                <CompetitionDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="products"
            element={
              <RequirePermission staffKey="products">
                <ProductsPage />
              </RequirePermission>
            }
          />
          <Route
            path="calendar"
            element={
              <RequirePermission staffKey={['calendar', 'my_members']}>
                <CalendarPage />
              </RequirePermission>
            }
          />
          <Route
            path="news"
            element={
              <RequirePermission staffKey="news">
                <NewsPage />
              </RequirePermission>
            }
          />
          <Route
            path="statistics"
            element={
              <RequirePermission staffKey="statistics">
                <StatisticsPage />
              </RequirePermission>
            }
          />
          <Route
            path="invoices"
            element={
              <RequirePermission staffKey="invoices">
                <InvoicesPage />
              </RequirePermission>
            }
          />
          <Route
            path="exports"
            element={
              <RequirePermission staffKey="exports">
                <ExportsPage />
              </RequirePermission>
            }
          />
        </Route>
      </Routes>
    </Sentry.ErrorBoundary>
  )
}
